import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { WheelEntry, WheelTheme } from '../../types/wheel';
import SpinResultModal from '../modals/SpinResultModal';

// Helper function to determine if a color is light or dark
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isLightColor = (color: string): boolean => {
  // Convert color to RGB
  let r, g, b;

  // Handle hex colors
  if (color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  }
  // Handle rgb colors
  else if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    } else {
      return false; // Default to assuming dark color
    }
  } else {
    return false; // Default to assuming dark color
  }

  // Calculate perceived brightness using the formula:
  // (0.299*R + 0.587*G + 0.114*B)
  const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255;

  // Return true if color is light (brightness > 0.5)
  return brightness > 0.5;
};

// Helper function to generate a circular segment path with multiple points along the arc
const generateCircularSegmentPath = (startAngle: number, endAngle: number): string => {
  // Start with the center point
  let path = 'polygon(50% 50%';

  // Number of points to use along the arc (more points = smoother curve)
  const numPoints = 20;

  // Add points along the arc from start to end angle
  for (let i = 0; i <= numPoints; i++) {
    const angle = startAngle + (i / numPoints) * (endAngle - startAngle);
    const angleRad = (angle * Math.PI) / 180;
    const x = 50 + 50 * Math.cos(angleRad);
    const y = 50 + 50 * Math.sin(angleRad);
    path += `, ${x}% ${y}%`;
  }

  // Close the path
  path += ')';
  return path;
};

interface WheelDisplayProps {
  entries: WheelEntry[];
  onSpinComplete?: (entry: WheelEntry) => void;
  size?: number;
  duration?: number;
  colorScheme?: WheelTheme | string;
  showSpinButton?: boolean;
  spinButtonText?: string;
  externalSpin?: boolean; // New prop to trigger spin from outside
  hideResultDisplay?: boolean; // New prop to hide the internal result display
}

const defaultColors = [
  '#EC4899', // primary
  '#8B5CF6', // secondary
  '#F59E0B', // accent
  '#EC4899', // primary
  '#8B5CF6', // secondary
  '#F59E0B', // accent
];

const WheelDisplay: React.FC<WheelDisplayProps> = ({
  entries,
  onSpinComplete,
  size = 400,
  duration = 5,
  colorScheme = 'default',
  showSpinButton = true,
  spinButtonText = 'Spin Wheel', // Changed from 'Preview Spin' to 'Spin Wheel'
  externalSpin = false,
  hideResultDisplay = false, // Changed to show result display by default
}) => {
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<WheelEntry | null>(null);
  const [rotation, setRotation] = useState(0);
  const [selectedSliceIndex, setSelectedSliceIndex] = useState<number | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);

  // Calculate the angle for each segment
  const segmentAngle = 360 / entries.length;

  // Function to spin the wheel
  const spinWheel = useCallback(() => {
    if (spinning || entries.length === 0) return;
    setSpinning(true);
    setResult(null);
    // Calculate a random rotation (at least 5 full rotations)
    const minRotation = 1800; // 5 full rotations
    const randomRotation = minRotation + Math.random() * 360;
    // Calculate the final position to determine the winner
    const finalRotation = rotation + randomRotation;
    setRotation(finalRotation);
    // Calculate which segment will be at the top when the wheel stops
    setTimeout(() => {
      // The wheel rotates clockwise, and the pointer is at the top (0 degrees)
      const normalizedRotation = finalRotation % 360;
      // Add a 90-degree offset to align the spinner with the correct segment
      // This is because the wheel's 0 degrees is at the top, but our calculation
      // needs to be adjusted to match the visual position
      const adjustedRotation = (normalizedRotation + 90) % 360;
      // Calculate which segment is at the top when the wheel stops
      const winningIndex = Math.floor(((360 - adjustedRotation) % 360) / segmentAngle);
      // Make sure the index is within the bounds of the entries array
      const safeIndex = winningIndex % entries.length;
      const winningEntry = entries[safeIndex];
      setResult(winningEntry);
      setSelectedSliceIndex(safeIndex);
      setSpinning(false);

      // Call onSpinComplete immediately to update parent component state
      if (onSpinComplete) {
        onSpinComplete(winningEntry);
      }

      // Show the result modal after a short delay for dramatic effect
      setTimeout(() => {
        setShowResultModal(true);
      }, 500); // Half second delay for dramatic effect
    }, duration * 1000);
  }, [spinning, entries, segmentAngle, rotation, duration, onSpinComplete]);

  // Reset the wheel when entries change
  useEffect(() => {
    setResult(null);
    setRotation(0);
    setSelectedSliceIndex(null);
    setShowResultModal(false);
  }, [entries]);

  // Handle external spin trigger - only spin once when externalSpin becomes true
  useEffect(() => {
    // Only respond to externalSpin becoming true
    if (externalSpin && !spinning && entries.length > 0) {
      spinWheel();
    }
  }, [externalSpin, spinning, entries.length, spinWheel]);

  // Function to calculate responsive font size based on wheel size and number of entries
  const calculateFontSize = () => {
    const baseSize = size || 400;
    const entryCount = entries.length;

    // Base font size calculation
    let fontSize = Math.max(12, Math.min(18, baseSize / 20));

    // Adjust based on number of entries
    if (entryCount > 12) {
      fontSize = Math.max(10, fontSize - (entryCount - 12) * 0.5);
    }

    return `${fontSize}px`;
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div
        className="relative w-full max-w-full"
        style={{
          width: size,
          height: size,
          maxWidth: '100%',
          maxHeight: '100vh',
        }}
      >
        {/* Wheel */}
        <motion.div
          ref={wheelRef}
          className="w-full h-full rounded-full overflow-hidden shadow-xl border-4 border-white relative"
          style={{ transformOrigin: 'center' }}
          animate={{
            rotate: rotation,
          }}
          transition={{
            duration: spinning ? duration : 0,
            ease: [0.2, 0.1, 0.1, 1],
          }}
        >
          {entries.map((entry, index) => {
            const startAngle = index * segmentAngle;
            const endAngle = (index + 1) * segmentAngle;
            // Use the colorScheme to determine the color palette
            const color =
              entry.color ||
              (colorScheme === 'default'
                ? defaultColors[index % defaultColors.length]
                : defaultColors[index % defaultColors.length]);
            return (
              <div
                key={entry.id}
                className={`absolute top-0 left-0 w-full h-full ${
                  selectedSliceIndex === index ? 'selected-slice' : ''
                }`}
                style={{
                  clipPath: generateCircularSegmentPath(startAngle, endAngle),
                  backgroundColor: color,
                }}
              >
                {/* Upright text label */}
                <div
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    transformOrigin: 'center center',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '50%', // Extends from center to edge
                      height: '20px',
                      transformOrigin: 'left center',
                      transform: `rotate(${startAngle + segmentAngle / 2}deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingLeft: '20px', // Add some padding from the center
                    }}
                  >
                    <div
                      className="wheel-text wheel-text-light"
                      style={{
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        fontSize: calculateFontSize(),
                      }}
                    >
                      {entry.emoji && <span className="mr-1">{entry.emoji}</span>}
                      <span>{entry.text}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
        {/* Pointer */}
        <div
          className="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-2 text-4xl"
          style={{
            zIndex: 10,
            color: '#EC4899', // Use primary color for better visibility
            textShadow: '0 0 5px rgba(0,0,0,0.5)', // Add shadow for contrast
            fontSize: '2.5rem', // Make it slightly larger
          }}
        >
          ▼
        </div>
      </div>
      {/* Spin button - conditionally rendered */}
      {showSpinButton && (
        <button
          onClick={spinWheel}
          disabled={spinning || entries.length === 0}
          className={`mt-6 btn-primary px-8 py-3 text-lg ${
            spinning ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {spinning ? 'Spinning...' : spinButtonText}
        </button>
      )}
      {/* Result modal */}
      <SpinResultModal
        isOpen={showResultModal && !hideResultDisplay}
        onClose={() => {
          setShowResultModal(false);
          console.log('Modal closed');
        }}
        result={result}
        confirmText="Confirm"
        spinAgainText="Spin Again"
        onSpinAgain={spinWheel}
      />
    </div>
  );
};

export default WheelDisplay;
